import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal, Field, Select } from "../../components/ui-modal";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import AuthImg from "../../components/AuthImg";

const BLANK = { client_id: "", name: "", function: "", email: "", phone: "", mobile: "", notes: "", is_referent: false };

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [refOnly, setRefOnly] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/admin/contacts").then((r) => setContacts(r.data));
  useEffect(() => { load(); api.get("/admin/clients").then((r) => setClients(r.data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Le nom est requis");
    const payload = { name: form.name, function: form.function, email: form.email, phone: form.phone, mobile: form.mobile, notes: form.notes };
    if (form.client_id) payload.links = [{ client_id: form.client_id, is_referent: form.is_referent }];
    try {
      await api.post("/admin/contacts", payload);
      toast.success("Contact créé"); setCreating(false); setForm(BLANK); load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Erreur"); }
  };

  const filtered = contacts.filter((c) => {
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = [c.name, c.function, c.email, c.phone, c.mobile, ...(c.companies || []).map((x) => x.name)].map((x) => (x || "").toLowerCase()).join(" ");
      if (!hay.includes(q)) return false;
    }
    if (clientFilter && !(c.companies || []).some((x) => x.client_id === clientFilter)) return false;
    if (refOnly && !(c.companies || []).some((x) => x.is_referent)) return false;
    return true;
  });
  const activeFilters = search || clientFilter || refOnly;
  const resetFilters = () => { setSearch(""); setClientFilter(""); setRefOnly(false); };
  const selCls = "bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none max-w-full";

  return (
    <div>
      <PageTitle overline="Gestion" title="Contacts" action={<button onClick={() => setCreating(true)} data-testid="new-contact-btn" className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover flex items-center gap-2"><Icons.Plus size={16} /> Nouveau contact</button>} />

      <div className="bg-sable rounded-sm border border-marine/10 p-4 mb-4" data-testid="contact-filters">
        <div className="relative mb-3">
          <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-marine" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contact-search"
            placeholder="Rechercher par nom, fonction, e-mail, entreprise..."
            className="w-full bg-creme border border-marine/15 rounded-sm pl-9 pr-3 py-2.5 text-sm text-marine focus:border-marine/50 outline-none" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Filtrer par entreprise" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="filter-client" className={selCls}>
            <option value="">Toutes les entreprises</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-marine cursor-pointer px-2">
            <input type="checkbox" checked={refOnly} onChange={(e) => setRefOnly(e.target.checked)} data-testid="filter-referent" className="w-4 h-4 accent-bordeaux" /> Référents seulement
          </label>
          {activeFilters ? (
            <button onClick={resetFilters} data-testid="filter-reset" className="text-xs px-3 py-2 text-rouge hover:underline inline-flex items-center gap-1"><Icons.X size={16} /> Réinitialiser</button>
          ) : null}
          <span className="ml-auto text-xs text-marine/80" data-testid="contact-count">{filtered.length} / {contacts.length} contact(s)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3" data-testid="contact-list">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`contact-row-${c.id}`} className="bg-sable rounded-sm border border-marine/10 p-5 [overflow-wrap:anywhere]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-marine-50 text-marine flex items-center justify-center overflow-hidden shrink-0 font-display">{c.avatar_url ? <AuthImg path={c.avatar_url} alt={c.name} className="w-full h-full object-cover" /> : (c.name || "?").charAt(0).toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="font-display text-lg text-marine">{c.name}</div>
                  {c.function && <div className="text-xs font-semibold text-bordeaux mt-0.5">{c.function}</div>}
                  <div className="text-sm text-marine/80 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                    {c.email && <span className="flex items-center gap-1"><Icons.Mail size={16} className="text-marine" /> {c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1"><Icons.Phone size={16} className="text-marine" /> {c.phone}</span>}
                    {c.mobile && <span className="flex items-center gap-1"><Icons.Smartphone size={16} className="text-marine" /> {c.mobile}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(c.companies || []).map((co) => (
                      <button key={co.client_id} onClick={() => nav(`/admin/clients/${co.client_id}`)} data-testid={`contact-company-${c.id}-${co.client_id}`}
                        className={`text-[9px] px-2 py-0.5 rounded-sm inline-flex items-center gap-1 border transition-colors ${co.is_referent ? "bg-bordeaux-50 text-bordeaux border-bordeaux/40" : "bg-marine-50 text-marine border-transparent hover:border-marine/40"}`}>
                        {co.is_referent && <Icons.Star size={16} className="fill-bordeaux" />}<Icons.Building2 size={16} /> {co.name}
                      </button>
                    ))}
                    {(c.companies || []).length === 0 && <span className="text-[11px] text-marine/80 italic">Aucune entreprise associée</span>}
                  </div>
                </div>
              </div>
              <button onClick={() => nav(`/admin/contacts/${c.id}`)} data-testid={`manage-contact-${c.id}`} className="text-sm bg-marine text-white rounded-sm px-3 py-1.5 hover:bg-marine-hover transition-colors flex items-center gap-1 shrink-0">Gérer la fiche <Icons.ArrowRight size={16} /></button>
            </div>
          </div>
        ))}
        {contacts.length === 0 && <p className="text-sm text-marine/80">Aucun contact. Créez-en un ou ajoutez-en depuis une fiche entreprise.</p>}
        {contacts.length > 0 && filtered.length === 0 && <p className="text-sm text-marine/80" data-testid="contact-empty-filter">Aucun contact ne correspond aux filtres.</p>}
      </div>

      {creating && (
        <Modal onClose={() => setCreating(false)} title="Nouveau contact">
          <form onSubmit={create} className="space-y-3">
            <Field label="Nom et prénom"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="inp" placeholder="Nom Prénom" /></Field>
            <Field label="Fonction / Poste"><input value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })} data-testid="contact-function" className="inp" placeholder="Commercial, Directrice..." /></Field>
            <Field label="Adresse e-mail"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="inp" placeholder="prenom@exemple.com" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Téléphone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" className="inp" placeholder="+971 ..." /></Field>
              <Field label="Mobile"><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="contact-mobile" className="inp" placeholder="+971 ..." /></Field>
            </div>
            <Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes" className="inp min-h-[60px]" placeholder="Notes internes..." /></Field>
            <div className="pt-2 border-t border-marine/10">
              <Select label="Associer à une entreprise (optionnel)" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} options={[{ v: "", l: "— Aucune —" }, ...clients.map((c) => ({ v: c.id, l: c.name }))]} testid="contact-client" />
              {form.client_id && <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={form.is_referent} onChange={(e) => setForm({ ...form, is_referent: e.target.checked })} data-testid="contact-referent" className="w-4 h-4 accent-bordeaux" /> Contact référent de cette entreprise</label>}
            </div>
            <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setCreating(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button><button data-testid="contact-submit" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Créer</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
